/**
 * Der Check-in-Bereich: Ablage der Unterlagen für die Tauchbasis.
 *
 * **Ohne Passwort und ohne Sperre.** Das war einmal anders — der Bereich lag
 * verschlüsselt hinter einer Passphrase und sperrte sich nach fünf Minuten von
 * selbst. Die Rechnung ging nicht auf, aus drei Gründen:
 *
 * * Das Gerät ist schon geschützt — der Rechner über die Anmeldung, das
 *   Telefon über die Entsperrung. Ein zweites Passwort schützt dieselbe Sache
 *   ein zweites Mal.
 * * Genau diese Unterlagen gibt man an jeder Tauchbasis aus der Hand, wo sie
 *   kopiert und in irgendeinen Ordner gelegt werden.
 * * Und die Sperre kostete mehr, als sie brachte: Auf dem Telefon **verhinderte
 *   sie den Foto-Upload**. Wer die Dateiauswahl öffnet, schickt die Seite in den
 *   Hintergrund; die Automatik warf daraufhin den Schlüssel weg, und das
 *   Speichern scheiterte an „Ohne Schlüssel wird nichts gespeichert" — während
 *   die Beilage im Formular längst dastand. Am Browser nachgestellt, nicht
 *   vermutet.
 *
 * **Was das kostet, wird nicht verschwiegen:** Der Bereich wandert in den
 * Cloud-Ordner und liegt dort jetzt im Klartext. Wer das Konto übernimmt, liest
 * die Passnummer. Das ist eine bewusste Entscheidung des Besitzers, kein
 * Versehen — sie steht auch in CLAUDE.md an der Stelle, wo vorher das Gegenteil
 * stand.
 *
 * Was verschlüsselt abgelegt wurde, holt `entschluesselAlles()` einmalig
 * heraus — danach ist der Tresor weg.
 */

import { entschluessle, entschluessleJson } from './krypto.js';

export const SPEICHER_CHECKIN = 'checkin';
export const SPEICHER_TRESOR = 'tresor';

/**
 * Die Arten und ihre Felder.
 *
 * Jede Unterlage hat dasselbe offene Geruest — Art, Titel, Aussteller,
 * Gueltigkeit — und darunter ihre eigenen geheimen Felder. Die Beschriftungen
 * des Geruests wechseln mit der Art: Ein Brevet hat ein „Datum" und kein
 * „Gueltig bis", ein Pass ist „Ausgestellt am".
 *
 * **Die Trennlinie zwischen offen und geheim ist eine Entscheidung, keine
 * Bequemlichkeit.** Offen bleibt, was sagt *was* man hat („PADI Advanced,
 * gueltig bis 2027") — daran erkennt man im gesperrten Zustand, ob das Noetige
 * hinterlegt ist, und genau das wird an der Basis zuerst gefragt. Geheim ist
 * alles, was auf einem Ausweis steht: Nummern, Namen, Behoerden, Kontakte.
 *
 * Beim Reisepass steht deshalb **kein** offenes Ausstellerfeld: Die Behoerde
 * gehoert zum Ausweis, nicht zur Auskunft.
 */
export const ARTEN = [
  {
    schluessel: 'certification',
    name: 'Brevet',
    zeichen: 'rekord',
    titel: 'Brevet',
    titelBeispiel: 'Advanced Open Water',
    aussteller: 'Organisation',
    ausstellerBeispiel: 'PADI, SSI, CMAS',
    von: 'Datum',
    bis: null,
    geheim: [
      ['nummer', 'Zertifikats-Nr.'],
      ['instruktor', 'Instruktor'],
      ['instruktor_nr', 'Instruktor-Nr.'],
      ['notizen', 'Notizen'],
    ],
  },
  {
    schluessel: 'insurance',
    name: 'Versicherung',
    zeichen: 'checkin',
    titel: 'Versicherungsmodell',
    titelBeispiel: 'DAN Sport Silver',
    aussteller: 'Versicherer',
    ausstellerBeispiel: 'DAN Europe',
    von: 'Gültig von',
    bis: 'Gültig bis',
    geheim: [
      ['nummer', 'Versicherungs-Nr.'],
      ['notruf', 'Notfall-Nr.'],
      ['leistungen', 'Deckung'],
      ['notizen', 'Notizen'],
    ],
  },
  {
    schluessel: 'passport',
    name: 'Reisepass',
    zeichen: 'pass',
    titel: 'Bezeichnung',
    titelBeispiel: 'Schweizer Pass',
    aussteller: null,
    von: 'Ausgestellt am',
    bis: 'Gültig bis',
    geheim: [
      ['nummer', 'Reisepass-Nr.'],
      ['ausgestellt_von', 'Ausgestellt von'],
      ['notizen', 'Notizen'],
    ],
  },
  {
    schluessel: 'emergency',
    name: 'Notfallkontakt',
    zeichen: 'buddy',
    titel: 'Bezeichnung',
    titelBeispiel: 'Partnerin',
    aussteller: null,
    von: null,
    bis: null,
    geheim: [
      ['name', 'Name'],
      ['beziehung', 'Beziehung'],
      ['telefon', 'Telefon'],
      ['notizen', 'Notizen'],
    ],
  },
  {
    schluessel: 'medical',
    name: 'Medizinisches',
    zeichen: 'medizin',
    titel: 'Bezeichnung',
    titelBeispiel: 'Tauchtauglichkeit',
    aussteller: null,
    von: 'Untersucht am',
    bis: 'Gültig bis',
    geheim: [
      ['arzt', 'Arzt / Praxis'],
      ['blutgruppe', 'Blutgruppe'],
      ['allergien', 'Allergien'],
      ['medikamente', 'Medikamente'],
      ['notizen', 'Notizen'],
    ],
  },
];

const ART_NACH_SCHLUESSEL = new Map(ARTEN.map((a) => [a.schluessel, a]));

export function art(schluessel) {
  return ART_NACH_SCHLUESSEL.get(schluessel) ?? null;
}

/** Der Anzeigename einer Art — auch fuer eine, die es nicht mehr gibt. */
export function artname(schluessel) {
  return art(schluessel)?.name ?? schluessel ?? '';
}

/** Die geheimen Felder einer Art, als Paare. */
export function geheimfelder(schluessel) {
  return art(schluessel)?.geheim ?? [];
}

/**
 * Wer auf Aenderungen wartet.
 *
 * Die Tauchgaenge melden ueber `saetze.js:beiAenderung()`; der Check-in liegt
 * daneben und hat seinen eigenen Weg. Ohne dieses Signal muesste der Anwender
 * daran denken, nach jeder Aenderung von Hand zu sichern — und genau darauf
 * ist kein Verlass, wenn der Browserspeicher geleert wird.
 */
const zuhoerer = new Set();

export function beiCheckinAenderung(rueckruf) {
  zuhoerer.add(rueckruf);
  return () => zuhoerer.delete(rueckruf);
}

function melde() {
  for (const rueckruf of zuhoerer) {
    try {
      rueckruf();
    } catch {
      // Ein Zuhoerer, der stolpert, darf das Speichern nicht mitreissen.
    }
  }
}

function abschluss(transaktion) {
  return new Promise((erfuellen, ablehnen) => {
    transaktion.oncomplete = () => erfuellen();
    transaktion.onerror = () => ablehnen(transaktion.error);
    transaktion.onabort = () => ablehnen(transaktion.error);
  });
}

function ergebnis(anfrage) {
  return new Promise((erfuellen, ablehnen) => {
    anfrage.onsuccess = () => erfuellen(anfrage.result);
    anfrage.onerror = () => ablehnen(anfrage.error);
  });
}

export async function holeTresor(db) {
  const t = db.transaction(SPEICHER_TRESOR, 'readonly');
  return ergebnis(t.objectStore(SPEICHER_TRESOR).get('tresor'));
}

export async function setzeTresor(db, tresor) {
  const t = db.transaction(SPEICHER_TRESOR, 'readwrite');
  t.objectStore(SPEICHER_TRESOR).put(tresor, 'tresor');
  await abschluss(t);
}

/**
 * Den Tresor wegräumen, ohne etwas zu entschlüsseln.
 *
 * Der Weg für den, der sein altes Passwort nicht mehr weiss: Ohne es ist der
 * Geheimtext nicht zu retten, und der Reiter bliebe sonst für immer in der
 * Übernahme stecken.
 */
export async function vergissTresor(db) {
  const t = db.transaction(SPEICHER_TRESOR, 'readwrite');
  t.objectStore(SPEICHER_TRESOR).delete('tresor');
  await abschluss(t);
  melde();
}

/**
 * Beilagen als Base64, nicht als Bytefeld.
 *
 * Sie wandern durch eine JSON-Datei, und dort steht ein Bytefeld als
 * Zahlenliste mit Komma — viermal so breit wie im Speicher. Base64 kostet ein
 * Drittel Aufschlag statt dreihundert Prozent, und es ist überall dieselbe
 * Darstellung: in der Datenbank, in der Sicherung, im Abgleich.
 */
export function alsText(bytes) {
  const roh = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let text = '';
  // Stückweise, weil `String.fromCharCode(...)` mit einem Megabyte an
  // Argumenten den Aufrufstapel sprengt.
  for (let i = 0; i < roh.length; i += 8192) {
    text += String.fromCharCode(...roh.subarray(i, i + 8192));
  }
  return btoa(text);
}

export function ausText(text) {
  const roh = atob(String(text ?? ''));
  const bytes = new Uint8Array(roh.length);
  for (let i = 0; i < roh.length; i += 1) bytes[i] = roh.charCodeAt(i);
  return bytes;
}

/**
 * Die Beilagen eines Satzes, in der heutigen Form.
 *
 * Drei Formen hat es gegeben: ein einzelnes `foto`, eine Liste aus
 * Geheimtext-Paketen, und die heutige Liste mit Base64. Die ersten beiden
 * lassen sich hier nicht lesen — sie brauchen den Schlüssel und laufen über
 * `entschluesselAlles()`. Was hier zurückkommt, ist immer lesbar.
 */
export function anhaengeVon(satz) {
  return (satz?.anhaenge ?? []).filter((anhang) => typeof anhang?.daten === 'string');
}

/** Alle Unterlagen, vollständig — es gibt nichts mehr zu verbergen. */
export async function liste(db) {
  const t = db.transaction(SPEICHER_CHECKIN, 'readonly');
  const alle = await ergebnis(t.objectStore(SPEICHER_CHECKIN).getAll());
  return alle
    .map((satz) => ({ ...satz, felder: satz.felder ?? {}, anhaenge: anhaengeVon(satz) }))
    .sort((a, b) => (a.art ?? '').localeCompare(b.art ?? '')
      || (a.titel ?? '').localeCompare(b.titel ?? ''));
}

/**
 * Eine Unterlage speichern — weiterhin der einzige Schreibweg.
 *
 * `felder` sind die art-eigenen Angaben, `anhaenge` die vollständige gewünschte
 * Liste der Beilagen. Ein Eintrag ist entweder **neu** (`{ name, typ, roh }`
 * mit `roh` als ArrayBuffer) oder **schon abgelegt** (`{ id, name, typ, daten }`)
 * und wandert unverändert mit. Was der Aufrufer weglässt, ist gelöscht — das
 * ist die einzige Form, in der „eine Beilage entfernen" keinen zweiten
 * Schreibweg braucht.
 */
export async function speichere(db, unterlage, felder, anhaenge = []) {
  const satz = {
    id: unterlage.id ?? crypto.randomUUID(),
    art: unterlage.art,
    titel: unterlage.titel ?? '',
    aussteller: unterlage.aussteller ?? '',
    gueltig_von: unterlage.gueltig_von ?? '',
    gueltig_bis: unterlage.gueltig_bis ?? '',
    geaendert: new Date().toISOString(),
    felder: felder ?? {},
    anhaenge: anhaenge.map((anhang) => (anhang.roh
      ? {
        id: anhang.id ?? crypto.randomUUID(),
        name: anhang.name ?? 'Beilage',
        typ: anhang.typ ?? 'application/octet-stream',
        groesse: anhang.roh.byteLength ?? null,
        daten: alsText(anhang.roh),
      }
      : {
        id: anhang.id,
        name: anhang.name,
        typ: anhang.typ,
        groesse: anhang.groesse ?? null,
        daten: anhang.daten,
      })),
  };
  const t = db.transaction(SPEICHER_CHECKIN, 'readwrite');
  t.objectStore(SPEICHER_CHECKIN).put(satz);
  await abschluss(t);
  melde();
  return satz.id;
}

/**
 * Der einmalige Weg aus dem Tresor heraus.
 *
 * Wer den Bereich unter der alten Fassung eingerichtet hat, gibt sein Passwort
 * noch ein einziges Mal ein; danach steht alles im Klartext da und der Tresor
 * ist weg. Ohne diesen Weg wäre der Umbau ein Datenverlust.
 */
export async function entschluesselAlles(db, schluessel) {
  const alle = await ergebnis(
    db.transaction(SPEICHER_CHECKIN, 'readonly').objectStore(SPEICHER_CHECKIN).getAll(),
  );
  const neu = [];
  for (const satz of alle) {
    const beilagen = [];
    for (const anhang of satz.anhaenge ?? []) {
      beilagen.push(anhang.daten?.geheim
        ? { ...anhang, daten: alsText(await entschluessle(schluessel, anhang.daten)) }
        : anhang);
    }
    if (!Array.isArray(satz.anhaenge) && satz.foto) {
      beilagen.push({
        id: crypto.randomUUID(),
        name: 'Foto',
        typ: satz.fotoTyp ?? 'image/jpeg',
        groesse: null,
        daten: alsText(await entschluessle(schluessel, satz.foto)),
      });
    }
    const { payload, foto, fotoTyp, ...offen } = satz;
    neu.push({
      ...offen,
      felder: payload ? await entschluessleJson(schluessel, payload) : (satz.felder ?? {}),
      anhaenge: beilagen,
    });
  }

  const t = db.transaction([SPEICHER_CHECKIN, SPEICHER_TRESOR], 'readwrite');
  const laden = t.objectStore(SPEICHER_CHECKIN);
  for (const satz of neu) laden.put(satz);
  t.objectStore(SPEICHER_TRESOR).delete('tresor');
  await abschluss(t);
  melde();
  return neu.length;
}

/**
 * Löschen hinterlässt einen Grabstein.
 *
 * Ohne ihn käme die Unterlage beim nächsten Geräte-Abgleich vom anderen Gerät
 * zurück — dort steht sie ja noch. Der Grabstein trägt nur Kennung und
 * Zeitpunkt, nichts Schützenswertes.
 */
export async function loesche(db, id) {
  const t = db.transaction([SPEICHER_CHECKIN, SPEICHER_TRESOR], 'readwrite');
  t.objectStore(SPEICHER_CHECKIN).delete(id);
  const tresorLaden = t.objectStore(SPEICHER_TRESOR);
  const bisher = await ergebnis(tresorLaden.get('grabsteine')) ?? [];
  const ohne = bisher.filter((g) => g.id !== id);
  tresorLaden.put([...ohne, { id, geaendert: new Date().toISOString() }], 'grabsteine');
  await abschluss(t);
  melde();
}

export async function holeGrabsteine(db) {
  const t = db.transaction(SPEICHER_TRESOR, 'readonly');
  return (await ergebnis(t.objectStore(SPEICHER_TRESOR).get('grabsteine'))) ?? [];
}

/**
 * Die Live-Kennzahlen für die Tauchbasis: Anzahl Tauchgänge, letzter Tauchgang,
 * Tauchgänge der letzten zwölf Monate. Nicht schützenswert, deshalb auch im
 * gesperrten Zustand sichtbar — genau die Angaben, nach denen an der Basis
 * gefragt wird.
 */
export function kennzahlen(tauchgaenge, jetzt = new Date()) {
  const zeiten = tauchgaenge.map((tg) => tg.start_utc).filter(Boolean).sort();
  const grenze = new Date(jetzt);
  grenze.setFullYear(grenze.getFullYear() - 1);
  const grenztext = grenze.toISOString();
  return {
    anzahl: tauchgaenge.length,
    letzter: zeiten[zeiten.length - 1] ?? null,
    erster: zeiten[0] ?? null,
    letzte12Monate: zeiten.filter((zeit) => zeit >= grenztext).length,
  };
}

/** Läuft eine Unterlage in den nächsten 90 Tagen ab? */
export function laeuftAb(unterlage, jetzt = new Date()) {
  if (!unterlage.gueltig_bis) return false;
  const ende = new Date(unterlage.gueltig_bis);
  if (Number.isNaN(ende.getTime())) return false;
  const tage = (ende - jetzt) / 86400000;
  return tage <= 90;
}

/**
 * Die Unterlagen nach Art gruppiert — in der Reihenfolge von `ARTEN`.
 *
 * Eine flache Liste aus „Unterlagen" beantwortet die Frage nicht, die an der
 * Basis gestellt wird. Dort heisst es „hast du dein Brevet dabei?" und nicht
 * „was hast du so hinterlegt?". Leere Gruppen bleiben stehen: Dass kein
 * Reisepass hinterlegt ist, ist eine Auskunft und keine Luecke.
 */
export function gruppiere(unterlagen = []) {
  return ARTEN.map((eineArt) => ({
    art: eineArt,
    eintraege: unterlagen
      .filter((u) => u.art === eineArt.schluessel)
      .sort((a, b) => ordnung(eineArt, b).localeCompare(ordnung(eineArt, a))
        || (a.titel ?? '').localeCompare(b.titel ?? '')),
  }));
}

/**
 * Wonach eine Gruppe sortiert — und warum nicht nach dem Namen.
 *
 * Vier Brevets alphabetisch sind Advanced (2011), Nitrox (2015), Open Water
 * (2009), Rescue (2014): eine Reihenfolge, die es im Leben des Tauchers nie
 * gab. Sortiert wird deshalb nach dem Datum, **jüngstes zuerst** — beim Brevet
 * ist das die höchste Qualifikation, bei Versicherung und Pass der Satz, der
 * gerade gilt. Genau die werden am Tresen gezeigt.
 *
 * Massgebend ist das Datum, das die Art überhaupt führt: das Ende, wo es eines
 * gibt, sonst der Anfang. Wer keins hat (Notfallkontakt, Medizinisches ohne
 * Datum), fällt auf den Namen zurück und steht hinten — leer sortiert vor
 * jedem Datum, deshalb dreht der Vergleich `b` gegen `a`.
 */
function ordnung(eineArt, unterlage) {
  const feld = eineArt.bis ? 'gueltig_bis' : 'gueltig_von';
  return String(unterlage?.[feld] ?? '');
}

/** „vor 3 Minuten", „vor 2 Tagen" — grob genug, um nie falsch zu sein. */
export function verstrichen(zeitpunkt, jetzt = new Date()) {
  const dann = new Date(zeitpunkt);
  if (Number.isNaN(dann.getTime())) return '';
  const minuten = Math.max(0, Math.round((jetzt - dann) / 60000));
  if (minuten < 1) return 'gerade eben';
  if (minuten < 60) return `vor ${minuten} Minute${minuten === 1 ? '' : 'n'}`;
  const stunden = Math.round(minuten / 60);
  if (stunden < 24) return `vor ${stunden} Stunde${stunden === 1 ? '' : 'n'}`;
  const tage = Math.round(stunden / 24);
  return `vor ${tage} Tag${tage === 1 ? '' : 'en'}`;
}

/**
 * Wo liegen die Unterlagen — und was fehlt dazu?
 *
 * Der Satz, der diesen Umbau ausgeloest hat, lautete: „Es kann nicht sein,
 * dass die Daten weg sind, wenn der Browser-Cache geloescht wird." Er hat
 * recht, und die Antwort ist nicht nur eine Sicherung, sondern eine Zeile, die
 * sagt, ob es eine gibt. Ein Bereich, der stillschweigend nur im Browser
 * liegt, ist eine Falle.
 */
export function sicherungslage({
  anbieter = null, verbunden = false, zuletzt = null, jetzt = new Date(),
} = {}) {
  if (!anbieter || anbieter === 'lokal') {
    return {
      stufe: 'keine',
      kurz: 'Nur auf diesem Gerät',
      text: 'Nur auf diesem Gerät. Wird der Browserspeicher geleert, sind die '
        + 'Unterlagen weg — auch mit dem richtigen Passwort.',
      knopf: 'Sicherung einrichten',
    };
  }
  if (!verbunden) {
    return {
      stufe: 'getrennt',
      kurz: `${anbieter} nicht verbunden`,
      text: `${anbieter} ist eingestellt, aber nicht verbunden. Bis zur Anmeldung `
        + 'bleibt jede Änderung nur auf diesem Gerät.',
      knopf: 'Verbinden',
    };
  }
  return {
    stufe: 'gesichert',
    kurz: zuletzt ? `${anbieter} · ${verstrichen(zuletzt, jetzt)}` : `${anbieter} · noch nie`,
    text: zuletzt
      ? `Gesichert in ${anbieter} · ${verstrichen(zuletzt, jetzt)}`
      : `Verbunden mit ${anbieter} · noch nicht gesichert`,
    knopf: 'Jetzt sichern',
  };
}

/** Lesbare Groesse einer Beilage. */
export function groesse(bytes) {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
